import React, { useMemo, useEffect, useState, useCallback } from "react";
import { useTable, useFilters, usePagination } from "react-table";
import { Badge } from "react-bootstrap";

import { ResidenteService } from "services/ResidenteService";
import { getDefaultParams } from "services/DefaultParamets";
import Tables from "jsx/components/CitoPlus/components/tables";
import SearchInput from "jsx/components/CitoPlus/components/input_search";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import ImageLightbox from "jsx/components/CitoPlus/components/ImageLightbox";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";

import "../../../table/FilteringTable/filtering.css";

const CorrespondenciaList = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState("");
  const [sortField, setSortField] = useState("");
  const [order, setOrder] = useState("asc");

  const [nextUrl, setNextUrl] = useState(null);
  const [previousUrl, setPreviousUrl] = useState(null);

  const [defaultParams] = useState(getDefaultParams);

  const getEstadoBadge = (estado) => {
    switch (estado) {
      case 0:
        return <Badge bg="warning">Llegada</Badge>;
      case 1:
        return <Badge bg="info">Notificado</Badge>;
      case 2:
        return <Badge bg="success">Recogido</Badge>;
      default:
        return <Badge bg="secondary">Desconocido</Badge>;
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "foto",
        Header: "Foto",
        accessor: "foto",
        disableFilters: true,
        disableSortable: true,
        Cell: ({ value }) => <ImageLightbox src={value} width={60} />,
      },
      {
        id: "created_at",
        Header: "Fecha Llegada",
        accessor: "created_at",
        disableFilters: true,
        disableSortable: false,
      },
      {
        id: "consecutivo",
        Header: "Consecutivo",
        accessor: "consecutivo",
        disableFilters: true,
        Cell: ({ value }) => value || "-",
      },
      {
        id: "obj",
        Header: "Descripción",
        accessor: "obj",
        disableFilters: true,
        Cell: ({ value }) => {
          if (!value) return "-";
          return value.length > 50 ? `${value.substring(0, 50)}...` : value;
        },
      },
      {
        id: "vigilante_ingreso",
        Header: "Recibido Por",
        accessor: "vigilante_ingreso",
        disableFilters: true,
      },
      {
        id: "recibido_nombre",
        Header: "Entregado A",
        accessor: "recibido_nombre",
        disableFilters: true,
        Cell: ({ value }) => value || "-",
      },
      {
        id: "estado",
        Header: "Estado",
        accessor: "estado",
        disableFilters: true,
        disableSortable: true,
        Cell: ({ value }) => getEstadoBadge(value),
      },
    ],
    [],
  );

  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => func(...args), delay);
    };
  };

  const debouncedfetchUrl = useCallback(
    debounce((pageIndex, search) => {
      fetchUrl(pageIndex, search);
    }, 500),
    [],
  );

  useEffect(() => {
    fetchUrl(1);
  }, []);

  const fetchUrl = async (pageIndex = 1, search = "", ordering = "") => {
    setLoading(true);
    try {
      const params = {
        ...defaultParams,
        page: pageIndex,
        search: search || searchValue,
        ordering: ordering,
      };

      const response = await ResidenteService.getCorrespondencia(params);

      setNextUrl(response.data.next);
      setPreviousUrl(response.data.previous);
      setTotalPages(response.data.total_pages || 1);
      setCurrentPage(response.data.current_page || pageIndex);
      setData(response.data.results || []);
    } catch (error) {
      handleErrors(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchValue(value);
    setLoading(true);
    debouncedfetchUrl(1, value);
  };

  const handleEnterPress = () => {
    fetchUrl(1, searchValue);
  };

  const handleClearClick = () => {
    setSearchValue("");
    fetchUrl(1, "");
  };

  const handleSortingChange = (column) => {
    const { id } = column;
    const newOrder =
      sortField === id ? (order === "asc" ? "desc" : "asc") : "asc";

    setSortField(id);
    setOrder(newOrder);
    setLoading(true);
    fetchUrl(1, searchValue, `${newOrder === "desc" ? "-" : ""}${id}`);
  };

  const tableInstance = useTable(
    {
      columns,
      data,
      manualPagination: true,
      pageCount: totalPages,
    },
    useFilters,
    usePagination,
  );

  const { page } = tableInstance;

  return (
    <>
      <div className="d-flex align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 font-w600 me-auto">Mi Correspondencia</h4>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-tools">
            <SearchInput
              value={searchValue}
              onSearchChange={handleSearchChange}
              onEnterPress={handleEnterPress}
              onClearClick={handleClearClick}
            />
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-4">
              <SpinnerCitoPlus text="Cargando correspondencia..." />
            </div>
          ) : data.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-envelope fa-3x text-muted mb-3"></i>
              <h5>No hay correspondencia registrada</h5>
              <p className="text-muted">
                Aquí aparecerá la correspondencia que llegue a tu unidad.
              </p>
            </div>
          ) : (
            <Tables
              columns={columns}
              data={data}
              loading={loading}
              totalPages={totalPages}
              currentPage={currentPage}
              fetchUrl={fetchUrl}
              handleSortingChange={handleSortingChange}
              nextUrl={nextUrl}
              previousUrl={previousUrl}
              sortField={sortField}
              order={order}
              page={page}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default CorrespondenciaList;
